/*
 * Shared helpers used by every per-module detail config. Kept JSX-free so the
 * config files stay pure data + small functions.
 */

export const CURRENCY_SYMBOL = { USD: '$', INR: '₹', EUR: '€', GBP: '£', AUD: '$', CAD: '$' };

export const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

export const has = (value) => !isEmpty(value);

export function capitalize(text) {
  const str = String(text);
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function asText(value) {
  if (Array.isArray(value)) return value.filter((item) => !isEmpty(item)).join(', ');
  return String(value);
}

export function symbol(currency) {
  if (isEmpty(currency)) return '';
  return CURRENCY_SYMBOL[String(currency).toUpperCase()] ?? String(currency);
}

// "Jun 17, 2026 | 07:31PM" — used by the header "Created on" line.
export function formatDateTime(value) {
  if (isEmpty(value)) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  const time = date
    .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toUpperCase();
  return `${day} | ${time}`;
}

// Formats a rate/budget into a compact string e.g. "₹34 - 55/Monthly" or "$80/Hour".
// Returns '' when there's no usable start value so callers can `has()`-guard it.
export function formatRate({ start, end, sym = '', unit } = {}) {
  if (isEmpty(start) || Number(start) <= 0) return '';
  const range = !isEmpty(end) && Number(end) > 0 ? `${start} - ${end}` : `${start}`;
  return `${sym}${range}${isEmpty(unit) ? '' : `/${unit}`}`;
}

// Light colour mapping for the status pill.
export function statusColor(value) {
  const key = String(value).toLowerCase();
  if (['active', 'open', 'hired', 'approved', 'fulfilled', 'submitted'].includes(key)) return 'success';
  if (['inactive', 'closed', 'rejected', 'high'].includes(key)) return 'error';
  if (['hold', 'on hold', 'pending', 'unread', 'medium'].includes(key)) return 'warning';
  return 'default';
}
